import React, { useRef, useState } from "react";
import ConfigProvider from "antd/es/config-provider";
import styled from "styled-components";
import plusIcon from "../../assets/svg/plus.svg";
import { NavBar } from "../../components/common/NavBar";
import { CompassModal, ModalFooter } from "../../components/common/Modal";
import {
  CompassMainHeader,
  CompassParagraph,
} from "../../components/common/styledComponents";
import { SideMenu } from "../../components/common/SideMenu";
import {
  BackgroundContainer,
  ElementContainer,
  MainColumn as Column,
  NavBarContainer,
  RowContainer,
} from "../../components/containers/styledComponents";
import { PageButton } from "../../components/visuals/PageButton";
import { CustomDropdown } from "../../components/visuals/PageFilter/PageFilter";
import { SearchBar } from "../../components/visuals/SearchBar";
import { VotingAlignment } from "../../components/common/Voting/VotingAlignment";
import { useStewardshipScreener } from "../../hooks/useStewardshipScreener";
import { MESSAGES } from "../../messages";
import { greyText } from "../../styles";
import {
  DEFAULT_FUND_FILTER,
  DEFAULT_MANAGER_FILTER,
} from "../../utils/PageFilterUtils";
import { StewardshipScatter } from "./EngagementMateriality/EngagementMaterialityScatter";
import { EngagementSankeyChart } from "./EngagementProgress/EngagementSankey";
import { ChoroplethChart } from "./EngagementsByGeo";
import {
  EngagementRecordForm,
  EngagementRecordFormRef,
} from "./EngagementRecordForm";
import { EngagementScreenerTable } from "./EngagementScreenerTable";
import { EngagementsByManagerBar } from "./EngagementsByManagerBar";
import { EngagementsByThemePie } from "./PieCharts/EngagementsBySectorPie";
import { VotingAgainstManagementPie } from "./PieCharts/VotingAgainstManagementPie";
import { VotingExecutionPie } from "./PieCharts/VotingExecutionPie";

const CardRowContainer = styled.div<{ pl?: string; pr?: string }>`
  display: flex;
  flex: 1;
  padding-left: ${({ pl }): string => pl || "0px"};
  padding-right: ${({ pr }): string => pr || "0px"};
`;

export const StewardshipHomepage = (): JSX.Element => {
  const [show, setShow] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [isFormLoading, setIsFormLoading] = useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const formRef = useRef<EngagementRecordFormRef>(null);
  const {
    fundOptions,
    filteredFundOptions,
    managerOptions,
    engagedCompaniesOptions,
    filterParams,
    isLoading,
    setSelectedFunds,
    setSelectedManagers,
    setSelectedCompanies,
    setScreenerPagination,
    selectedCompanies,
  } = useStewardshipScreener();

  const handleCloseModal = (): void => {
    setShowModal(false);
    setIsFormValid(false);
  };

  return (
    <BackgroundContainer>
      <Column flex={show ? "0 0 15%" : "0 0 0%"} show={show}>
        <SideMenu show={show} setShow={setShow} />
      </Column>
      <Column flex="1" bg="#F5F5F5" overflow={true}>
        <NavBarContainer>
          <RowContainer mt={1} mb={1}>
            <NavBar address="stewardship" />
          </RowContainer>
          <RowContainer ai="flex-start" jc="flex-start" mt={-15}>
            <CompassMainHeader pt={1} ml={12}>
              Stewardship
            </CompassMainHeader>
          </RowContainer>
          <RowContainer ai="flex-start" jc="flex-start" mt={1}>
            <CompassParagraph ml={12} color={greyText}>
              {MESSAGES.STEWARDSHIP_HOMEPAGE.DESCRIPTION}
            </CompassParagraph>
          </RowContainer>
          <RowContainer ai="center" jc="space-between" ml={8}>
            <ElementContainer ai="center">
              <CustomDropdown
                options={managerOptions}
                isLoading={isLoading}
                placeholder={DEFAULT_MANAGER_FILTER}
                isMultiSelect={true}
                onSelectOptions={setSelectedManagers}
              />
              <CustomDropdown
                options={filteredFundOptions}
                isLoading={isLoading}
                placeholder={DEFAULT_FUND_FILTER}
                isMultiSelect={true}
                onSelectOptions={setSelectedFunds}
              />
              <PageButton
                key={
                  engagedCompaniesOptions[0]?.key ||
                  "defaultCompaniesPageButtonKey"
                }
                isLoading={isLoading}
                content={
                  <SearchBar
                    options={engagedCompaniesOptions}
                    placeholder="Search company"
                    isMultiSelect={true}
                    onSelectOptions={setSelectedCompanies}
                    initialSelectedOptions={selectedCompanies}
                  />
                }
              />
            </ElementContainer>
            <ElementContainer mr={28}>
              <PageButton
                content={<CompassParagraph>Add Engagement</CompassParagraph>}
                image={plusIcon}
                action={(): void => setShowModal(true)}
                isLoading={isLoading}
                he={true}
              />
            </ElementContainer>
          </RowContainer>
        </NavBarContainer>
        <RowContainer>
          <EngagementsByManagerBar filters={filterParams} />
        </RowContainer>
        <RowContainer mt={-1} ai="flex-start">
          <CardRowContainer pl="8px">
            <ChoroplethChart filters={filterParams} />
          </CardRowContainer>
          <CardRowContainer pr="8px">
            <EngagementsByThemePie filters={filterParams} />
          </CardRowContainer>
        </RowContainer>
        <RowContainer mt={-1}>
          <EngagementScreenerTable
            filterParams={filterParams}
            fundOptions={fundOptions}
            managerOptions={managerOptions}
            areOptionsLoading={isLoading}
            totalRecords={
              selectedCompanies.length || engagedCompaniesOptions.length
            }
            handlePagination={setScreenerPagination}
          />
        </RowContainer>
        <RowContainer ai="flex-start">
          <StewardshipScatter filters={filterParams} />
        </RowContainer>
        <RowContainer>
          <EngagementSankeyChart filters={filterParams} />
        </RowContainer>
        <RowContainer mb={-1}>
          <VotingAlignment filters={filterParams} companySummaryData={null} />
        </RowContainer>
        <RowContainer mt={-1} ai="flex-start">
          <CardRowContainer pl="8px">
            <VotingAgainstManagementPie filters={filterParams} />
          </CardRowContainer>
          <CardRowContainer pr="8px">
            <VotingExecutionPie filters={filterParams} />
          </CardRowContainer>
        </RowContainer>
      </Column>
      <CompassModal
        show={showModal}
        handleClose={handleCloseModal}
        size="lg"
        title="Create Engagement"
        titleIcon={plusIcon}
        modalType="lg"
        footer={
          <ModalFooter
            onClose={handleCloseModal}
            onSubmit={(): void => formRef.current?.submit()}
            submitText="Create Engagement Record"
            isLoading={isFormLoading}
            submitDisabled={!isFormValid}
            showDivider={false}
          />
        }
      >
        <ConfigProvider
          getPopupContainer={(
            triggerNode: HTMLElement | undefined,
          ): HTMLElement => triggerNode?.parentNode as HTMLElement}
        >
          <EngagementRecordForm
            ref={formRef}
            onSuccess={handleCloseModal}
            onLoadingChange={setIsFormLoading}
            onValidityChange={setIsFormValid}
          />
        </ConfigProvider>
      </CompassModal>
    </BackgroundContainer>
  );
};
